import { ClientEvent, Room } from 'matrix-js-sdk/src/matrix';
import { useState, useEffect, useCallback } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { isMainRoom, isBreakoutRoom, isMessengerCallRoom } from 'utils/roomHierarchyUtils';

export enum RoomType {
  MAIN = 'main',
  BREAKOUT = 'breakout',
  CHAT = 'chat',
  UNKNOWN = 'unknown',
}

const determineRoomType = (room: Room | null) => {
  if (!room) return RoomType.UNKNOWN;
  if (isMainRoom(room) || isMessengerCallRoom(room)) return RoomType.MAIN;
  if (isBreakoutRoom(room)) return RoomType.BREAKOUT;
  return RoomType.UNKNOWN;
};

const useRoomType = (roomId: string) => {
  const { matrixClient } = useMatrixContext();
  const [roomType, setRoomType] = useState<RoomType>(RoomType.UNKNOWN);

  const checkRoomType = useCallback((room: Room | null) => {
    const newType = determineRoomType(room);

    setRoomType(newType);
    return newType;
  }, []);

  useEffect(() => {
    checkRoomType(matrixClient.getRoom(roomId));

    const onSync = () => {
      const room = matrixClient.getRoom(roomId);
      const roomType = checkRoomType(room);

      if (roomType && roomType !== RoomType.UNKNOWN) matrixClient.off(ClientEvent.Sync, onSync);
    };
    matrixClient.on(ClientEvent.Sync, onSync);

    return () => {
      matrixClient.off(ClientEvent.Sync, onSync);
    };
  }, [checkRoomType, matrixClient, roomId]);

  return roomType;
};

export default useRoomType;
